/**
 * Morse — eine Stufe geschafft: die ruhige Karte und der Notschalter der Tonfolge. (16.10.2026)
 *
 * Martin: Beim Aufstieg eine kleine Feier. Die Karte nennt die neue Stufe und das neue Zeichen mit Morse-Bild und Merkwort;
 * sie bleibt stehen, bis man sie schließt (kein Verschwinden nach Zeit — wer die Augen zu hatte, soll sie noch sehen).
 * Sie erscheint einmal kurz (CSS, ohne bei „weniger Bewegung“) und läuft nicht weiter.
 *
 * Die Tonfolge spielt morse-view.ts über audio-feedback.ts (`playBeep("aufstieg")`). Notschalter, falls sie stört:
 * localStorage `vespera.morse.fanfare` = „aus“ (kein Schalter in den Einstellungen — so gewünscht).
 * ⚠️ Keine Inline-Styles: alles in style.css (`.morse-aufstieg…`).
 */
import { kochNeuesZeichen } from "../../shared/morse-koch.js";
import { MORSE_TABELLE } from "../../shared/morse.js";
import { musterBild, musterSprechen } from "./morse-bild.js";
import { merkwortAria, merkwortElement } from "./morse-merkwort.js";
import { el, knopf } from "./morse-teile.js";

/** Der Schlüssel des Notschalters. */
export const FANFARE_SCHLUESSEL = "vespera.morse.fanfare";

/** Darf die Tonfolge klingen? Nur „aus“ im Notschalter schaltet sie ab; ohne Speicher (Privatmodus) klingt sie. */
export function fanfareErlaubt(): boolean {
  try {
    return localStorage.getItem(FANFARE_SCHLUESSEL) !== "aus";
  } catch {
    return true;
  }
}

/** Die Karte „Stufe N geschafft — neu: X“. `schliesse` nimmt sie weg (der Knopf „Weiter üben“). */
export function baueAufstiegKarte(stufe: number, schliesse: () => void): HTMLElement {
  const neu = kochNeuesZeichen(stufe);
  const muster = MORSE_TABELLE[neu] ?? "";
  const karte = el("section", "morse-aufstieg");
  karte.setAttribute("role", "status");
  karte.setAttribute("aria-label", `Stufe ${stufe} geschafft`);
  const text = el("div", "morse-aufstieg-text");
  text.append(
    el("p", "morse-aufstieg-marke", "Geschafft"),
    el("h3", "morse-aufstieg-titel", `Stufe ${stufe}`),
    el("p", "morse-aufstieg-satz", `Neu dazu: ${neu} — ${musterSprechen(muster)}.`),
  );
  const zeichen = el("div", "morse-aufstieg-zeichen");
  zeichen.setAttribute("aria-hidden", "true");
  zeichen.append(el("span", "morse-aufstieg-name", neu), musterBild(muster, "l"));
  const merk = merkwortElement(neu, "morse-aufstieg-merk");
  if (merk) zeichen.appendChild(merk);
  const aria = merkwortAria(neu);
  if (aria) text.appendChild(el("p", "morse-nur-vorlesen", aria));
  const weiter = knopf("Weiter üben", "morse-knopf morse-aufstieg-zu", schliesse);
  karte.append(zeichen, text, weiter);
  return karte;
}
